import { useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useProject, useCreateProject, useUpdateProject } from '../../hooks/useProjects';
import { useClients } from '../../hooks/useClients';
import { useContactsByClient } from '../../hooks/useContacts';
import type { ProjectStatus } from '../../types';

const projectSchema = z
  .object({
    name: z.string().min(1, 'Project name is required').max(200),
    description: z.string().max(2000).optional().or(z.literal('')),
    client_id: z.string().min(1, 'Client is required'),
    start_date: z.string().min(1, 'Start date is required'),
    end_date: z.string().optional().or(z.literal('')),
    status: z.enum(['planning', 'active', 'on_hold', 'completed', 'cancelled', 'paused'] as const),
    contact_ids: z.array(z.string()).optional(),
  })
  .refine(
    (data) => {
      if (data.end_date && data.start_date) {
        return new Date(data.end_date) >= new Date(data.start_date);
      }
      return true;
    },
    { message: 'End date must be after start date', path: ['end_date'] },
  );

type ProjectFormData = z.infer<typeof projectSchema>;

const statusOptions: { value: ProjectStatus; label: string }[] = [
  { value: 'planning', label: 'Planning' },
  { value: 'active', label: 'Active' },
  { value: 'on_hold', label: 'On Hold' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'paused', label: 'Paused' },
];

export default function ProjectForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isEdit = !!id;
  const prefilledClientId = searchParams.get('client_id') || '';

  const { data: project, isLoading: projectLoading } = useProject(id || '');
  const { data: clientsData } = useClients({ pageSize: 100 });
  const createProject = useCreateProject();
  const updateProject = useUpdateProject(id || '');

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<ProjectFormData>({
    resolver: zodResolver(projectSchema),
    defaultValues: {
      name: '',
      description: '',
      client_id: prefilledClientId,
      start_date: '',
      end_date: '',
      status: 'planning',
      contact_ids: [],
    },
  });

  const selectedClientId = watch('client_id');
  const { data: clientContacts } = useContactsByClient(selectedClientId);

  // Populate form when editing
  useEffect(() => {
    if (isEdit && project) {
      reset({
        name: project.name,
        description: project.description || '',
        client_id: project.client_id,
        start_date: project.start_date?.split('T')[0] || '',
        end_date: project.end_date ? project.end_date.split('T')[0] : '',
        status: project.status,
        contact_ids: project.contacts?.map((c) => c.id) || [],
      });
    }
  }, [isEdit, project, reset]);

  const onSubmit = async (data: ProjectFormData) => {
    const payload = {
      ...data,
      description: data.description || undefined,
      end_date: data.end_date || undefined,
      contact_ids: data.contact_ids?.length ? data.contact_ids : undefined,
    };

    if (isEdit) {
      await updateProject.mutateAsync(payload);
    } else {
      await createProject.mutateAsync(payload);
    }
    navigate('/projects');
  };

  if (isEdit && projectLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-gray-500">Loading project...</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          {isEdit ? 'Edit Project' : 'New Project'}
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          {isEdit
            ? 'Update the project details below.'
            : 'Fill in the details to create a new project.'}
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Name */}
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700">
            Project Name *
          </label>
          <input
            id="name"
            type="text"
            {...register('name')}
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
          {errors.name && (
            <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
          )}
        </div>

        {/* Description */}
        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700">
            Description
          </label>
          <textarea
            id="description"
            rows={3}
            {...register('description')}
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
          {errors.description && (
            <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
          )}
        </div>

        {/* Client */}
        <div>
          <label htmlFor="client_id" className="block text-sm font-medium text-gray-700">
            Client *
          </label>
          <select
            id="client_id"
            {...register('client_id')}
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          >
            <option value="">Select a client</option>
            {clientsData?.data?.map((client) => (
              <option key={client.id} value={client.id}>
                {client.name}
              </option>
            ))}
          </select>
          {errors.client_id && (
            <p className="mt-1 text-sm text-red-600">{errors.client_id.message}</p>
          )}
        </div>

        {/* Dates */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="start_date" className="block text-sm font-medium text-gray-700">
              Start Date *
            </label>
            <input
              id="start_date"
              type="date"
              {...register('start_date')}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
            {errors.start_date && (
              <p className="mt-1 text-sm text-red-600">{errors.start_date.message}</p>
            )}
          </div>
          <div>
            <label htmlFor="end_date" className="block text-sm font-medium text-gray-700">
              End Date
            </label>
            <input
              id="end_date"
              type="date"
              {...register('end_date')}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
            {errors.end_date && (
              <p className="mt-1 text-sm text-red-600">{errors.end_date.message}</p>
            )}
          </div>
        </div>

        {/* Status */}
        <div>
          <label htmlFor="status" className="block text-sm font-medium text-gray-700">
            Status *
          </label>
          <select
            id="status"
            {...register('status')}
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          >
            {statusOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {errors.status && (
            <p className="mt-1 text-sm text-red-600">{errors.status.message}</p>
          )}
        </div>

        {/* Contact assignment (multi-select checkboxes) */}
        {selectedClientId && clientContacts && clientContacts.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Assign Contacts
            </label>
            <div className="max-h-48 overflow-y-auto rounded-md border border-gray-300 p-3 space-y-2">
              {clientContacts.map((contact) => (
                <label
                  key={contact.id}
                  className="flex items-center gap-2 text-sm text-gray-700"
                >
                  <input
                    type="checkbox"
                    value={contact.id}
                    onChange={(e) => {
                      const current = watch('contact_ids') || [];
                      if (e.target.checked) {
                        setValue('contact_ids', [...current, contact.id]);
                      } else {
                        setValue(
                          'contact_ids',
                          current.filter((cid) => cid !== contact.id),
                        );
                      }
                    }}
                    checked={(watch('contact_ids') || []).includes(contact.id)}
                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  {contact.first_name} {contact.last_name} ({contact.email})
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Form errors */}
        {(createProject.error || updateProject.error) && (
          <div className="rounded-md bg-red-50 p-4">
            <p className="text-sm text-red-700">
              {(createProject.error as Error)?.message ||
                (updateProject.error as Error)?.message ||
                'An error occurred. Please try again.'}
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <button
            type="button"
            onClick={() => navigate('/projects')}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting || createProject.isPending || updateProject.isPending}
            className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {isSubmitting || createProject.isPending || updateProject.isPending
              ? 'Saving...'
              : isEdit
                ? 'Update Project'
                : 'Create Project'}
          </button>
        </div>
      </form>
    </div>
  );
}
